import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, TextField, InputAdornment, CircularProgress } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { atom_tourState } from '@app/shared/state/tour.atom';
import { useTranslation } from 'react-i18next';
import { fetchAutocompleteSuggestions, AutocompletePrediction } from '@app/shared/services/autoComplete.service';
import { fetchPlaceDetails } from '@app/shared/services/placeDetails.service';
import { PlaceDetails } from '@app/shared/state/user-added-stops.atom';

const THEME_COLOR = '#D0552E';

interface PlaceSearchInlineProps {
  onPlaceSelected: (details: PlaceDetails) => void;
  onFocusChange?: (focused: boolean) => void;
}

export const PlaceSearchInline: React.FC<PlaceSearchInlineProps> = ({ onPlaceSelected, onFocusChange }) => {
  const { t } = useTranslation();
  const activeTour = useRecoilValue(atom_tourState);

  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState<AutocompletePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPrediction, setSelectedPrediction] = useState<AutocompletePrediction | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Track online/offline status
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Debounced search
  useEffect(() => {
    if (selectedPrediction && searchInput === selectedPrediction.description) {
      return;
    }

    setSelectedPrediction(null);
    const timeout = setTimeout(async () => {
      if (searchInput.trim().length >= 3) {
        setIsLoading(true);
        const sp = activeTour?.starting_point;
        const firstStop = activeTour?.stops?.[0]?.stop_location;
        const tourLocation = sp?.latitude && sp?.longitude
          ? { lat: sp.latitude, lng: sp.longitude }
          : firstStop?.latitude && firstStop?.longitude
            ? { lat: firstStop.latitude, lng: firstStop.longitude }
            : undefined;
        const results = await fetchAutocompleteSuggestions(searchInput, tourLocation, tourLocation ? 15000 : undefined, 'establishment', false);
        setSuggestions(results);
        setIsLoading(false);
      } else {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchInput, selectedPrediction, activeTour]);

  const handleFocus = () => {
    setIsFocused(true);
    onFocusChange?.(true);
  };

  const handleBlur = () => {
    // Delay to allow click on suggestions before hiding
    setTimeout(() => {
      setIsFocused(false);
      onFocusChange?.(false);
    }, 200);
  };

  // Handle selecting a suggestion -> fetch details and notify parent
  const handlePlaceSelect = async (place: AutocompletePrediction) => {
    inputRef.current?.blur();
    setSearchInput(place.description);
    setSuggestions([]);
    setSelectedPrediction(place);
    setIsLoading(true);

    const details = await fetchPlaceDetails(place.place_id);
    if (details) {
      onPlaceSelected(details);
      const displayName = details.name || place.description;
      setSearchInput(displayName);
      setSelectedPrediction({ ...place, description: displayName });
    }
    setIsLoading(false);
  };

  // Hide when offline
  if (!isOnline) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Search Input - always visible */}
      <TextField
        inputRef={inputRef}
        fullWidth
        placeholder={t('search_placeholder') || 'Search for an Address or Business'}
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        autoComplete="off"
        spellCheck={false}
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '20px',
            bgcolor: '#fff',
            border: '1px solid #e0e0e0',
            '& fieldset': { border: 'none' },
            fontSize: 14,
            px: '4px',
            '&.Mui-focused': {
              border: `1.5px solid ${THEME_COLOR}`,
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ ml: 0.5 }}>
              <i className="fa-regular fa-magnifying-glass" style={{ fontSize: 14, color: '#999' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {isLoading ? (
                <CircularProgress size={16} />
              ) : searchInput ? (
                <Box
                  onClick={() => {
                    setSearchInput('');
                    setSuggestions([]);
                    setSelectedPrediction(null);
                  }}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    bgcolor: '#e0e0e0',
                    '&:hover': { bgcolor: '#bdbdbd' },
                  }}
                >
                  <i className="fa-solid fa-xmark" style={{ fontSize: 9, color: '#666' }} />
                </Box>
              ) : null}
            </InputAdornment>
          ),
        }}
      />

      {/* Suggestions area - reserves height when focused */}
      <Box sx={{ minHeight: isFocused ? 200 : 0, maxHeight: 200, overflowY: 'auto' }}>
        {suggestions.length > 0 ? (
          suggestions.map((s) => (
            <Box
              key={s.place_id}
              onClick={() => handlePlaceSelect(s)}
              sx={{
                height: 40,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderRadius: '8px',
                '&:hover': { bgcolor: '#f9f9f9' },
              }}
            >
              <i className="fa-solid fa-location-dot" style={{ color: '#999', fontSize: 14, flexShrink: 0 }} />
              <Typography
                sx={{
                  fontSize: 13,
                  color: '#333',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  '& .highlight': {
                    color: THEME_COLOR,
                    fontWeight: 600,
                  }
                }}
                dangerouslySetInnerHTML={{
                  __html: s.description.replace(
                    new RegExp(`(${searchInput.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'),
                    '<span class="highlight">$1</span>'
                  )
                }}
              />
            </Box>
          ))
        ) : searchInput.length > 0 && !isLoading && !selectedPrediction ? (
          <Typography sx={{ textAlign: 'center', color: '#999', py: 1, fontSize: 13 }}>
            {t('no_results_found') || 'No results found'}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default PlaceSearchInline;
